import { DOCUMENT } from "@angular/common";
import { ElementRef, Inject, Injectable, OnInit, Optional, Renderer2, RendererFactory2 } from "@angular/core";
import { Meta, MetaDefinition } from "@angular/platform-browser";
import { first, firstValueFrom, Observable, ReplaySubject } from "rxjs";
import { PersonStruct } from "@src/app/models/interfaces/PersonStruct.interface";
import { WebSiteStruct } from "@src/app/models/interfaces/websiteStruct.interfaces";
import { KebabCasePipe } from "src/app/pipes/kebab-case.pipe";
import { NoticiasService } from "../noticias/noticias.service";
import { PlatformService } from "../platform/platform.service";

import { ConfigService } from "../config/config.service";
import { TitleWrapperService } from "../title-wrapper/title-wrapper.service";
import { CanonicalService } from "../canonical/canonical.service";
@Injectable()
export class SeoService implements OnInit {
  private API_KEY: string = "AIzaSyBM0xuS8ZyBqVNWyTtKb916hwvz2ako8UI";
  private readonly renderer2?: Renderer2;
  private readonly constantesGlobales: any;

  private readonly redes: ReplaySubject<any> = new ReplaySubject(1);

  constructor(
    @Inject(DOCUMENT) private readonly doc,
    private readonly meta: Meta,
    private readonly titleWrapperService: TitleWrapperService,
    @Optional() rendererFactory: RendererFactory2,
    private readonly kebabPipe: KebabCasePipe,
    private readonly noticiasService: NoticiasService,
    private readonly platformService: PlatformService,
    private readonly configService: ConfigService,
    private readonly canonicalService: CanonicalService,
  ) {
    if (rendererFactory) {
      this.renderer2 = rendererFactory?.createRenderer(null, null);
    }
    this.constantesGlobales = this.configService.getConstantesGlobales();
    this.initRedes();
  }

  async ngOnInit(): Promise<void> {
    try {
      this.insertaScriptWebsite();
      this.insertaScriptOrganization();
    } catch (error) {}
  }

  /**
   * Inicializa las redes sociales obteniendo la configuración desde la API de prensa.
   *
   * Este método utiliza el servicio `noticiasService` para obtener la configuración
   * de prensa correspondiente al ámbito definido en las constantes globales. Una vez
   * que se recibe la respuesta, actualiza el observable `redes` con los datos obtenidos.
   *
   * @returns {Promise<void>} Una promesa que se resuelve cuando se completa la operación.
   *
   * @throws {Error} Puede lanzar un error si ocurre algún problema al obtener los datos
   *                 desde la API o al procesar la respuesta.
   */
  async initRedes() {
    const $result = this.configService.getConfigPrensaApi(this.constantesGlobales.idAmbito);
    const data = await firstValueFrom($result);
    if (data) {
      this.redes.next(data);
    }
  }

  getRedes(): Observable<any> {
    return this.redes.asObservable();
  }

  createLinkForCanonicalURL() {
    const link: HTMLLinkElement = this.doc.createElement("link");
    link.setAttribute("rel", "canonical");
    this.doc.head.appendChild(link);
    link.setAttribute("href", this.doc.URL);
  }

  updateMetaTags(noticia: any, customUrl: string | null = null): void {
    const fecha = this.dateToIso(noticia.fecha);
    const url = customUrl ?? noticia.urlNoticia;
    const autor = noticia?.nombre_autor || this.constantesGlobales.siteName;

    const metaTagsArray: MetaDefinition[] = [
      // Descripción general
      { name: "description", content: noticia.entradillaVideo },

      // Open Graph
      { property: "og:title", content: noticia.tituloVideo },
      { property: "og:type", content: "article" },
      { property: "og:image", content: noticia.urlGenerica?.replace(/\.webp$/i, ".jpg") },
      { property: "og:url", content: url },
      { property: "url", content: url },
      { property: "og:description", content: noticia.entradillaVideo },
      { property: "og:site_name", content: this.constantesGlobales.siteName },

      // Twitter Card
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: noticia.tituloVideo },
      { name: "twitter:image", content: noticia.urlGenerica?.replace(/\.webp$/i, ".jpg") },

      // Article / Schema
      { property: "article:author", content: autor },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "article:published_time", content: fecha },
      { property: "article:modified_time", content: fecha },

      // Dublin Core
      { name: "DC.title", content: noticia.tituloVideo },
      { name: "DC.creator", content: autor },
      { name: "DC.publisher", content: this.constantesGlobales.siteName },
      { name: "DC.description", content: noticia.entradillaVideo },
      { name: "DC.subject", content: noticia.entradillaVideo },
      { name: "DC.date", content: fecha },
      { name: "DC.date.issued", content: fecha },
    ];

    this.canonicalService.updateCanonicalUrls(metaTagsArray);

    this.titleWrapperService.setTitle(`${noticia.tituloVideo} - ${this.constantesGlobales.siteName}`);
    this.doc.querySelector('[rel="canonical"]')?.setAttribute("href", noticia.urlCanonica);
  }

  // SECCION GOOGLE SCHEMA MARKUP - DATOS ESTRUCTURADOS

  // FUNCIONES SCHEMAS AUXILIARES

  /**
   * La función crea un schema de imagen con una URL determinada.
   * @param {string} url - El parámetro `url` es una cadena que representa la URL de la imagen.
   * @returns un objeto con las propiedades '@type' y 'url'.
   */
  private creaImageSchema(url: string): any {
    return {
      "@type": "ImageObject",
      url,
    };
  }

  /**
   * La función crea un schema para una organización con sus propiedades.
   * @returns un objeto con las propiedades de la organización.
   */
  private creaOrganizationSchema(): any {
    const organization = {
      "@type": ["NewsMediaOrganization", "Organization"],
      name: this.constantesGlobales.siteName,
      url: this.constantesGlobales.siteDomain,
      logo: this.creaImageSchema(`${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.logoMain}`),
    };

    this.getRedes().subscribe((redes) => {
      if (redes) {
        const redesUrls = [];
        if (redes.facebook) {
          redesUrls.push(redes.facebook);
        }
        if (redes.twitter) {
          redesUrls.push(redes.twitter);
        }
        if (redes.insta) {
          redesUrls.push(redes.insta);
        }
        if (redes.telegram) {
          redesUrls.push(redes.telegram);
        }
        if (redes.yt) {
          redesUrls.push(redes.yt);
        }
        if (redesUrls.length > 0) {
          organization["sameAs"] = redesUrls;
        }
      }
      return organization;
    });
  }

  /**
   * La función crea un schema para un autor de un artículo de noticias.
   * @param {any} noticia - El parámetro "noticia" es un objeto que representa un artículo de noticias.
   * @returns un objeto con las propiedades '@tipo', 'nombre' y 'url'. El valor de '@type' se establece
   * en 'Persona'. **/
  private creaAuthorSchema(noticia: any): any {
    let autorNoticia = noticia?.nombre_autor;
    let urlAutor = this.constantesGlobales.siteDomain + "/autor/" + noticia.id_autor + "/" + this.kebabPipe.transform(noticia.nombre_autor);
    if (!autorNoticia) {
      autorNoticia = this.constantesGlobales.siteName;
      urlAutor = this.constantesGlobales.siteDomain;
    }
    return {
      "@type": "Person",
      name: autorNoticia,
      url: urlAutor,
    };
  }

  //FUNCIONES SCHEMAS PRINCIPALES

  /**
   * La función `insertaScriptNoticia` inserta un script en el documento HTML con el schema para un artículo de noticias.
   * @param {any} noticia - El parámetro "noticia" es un objeto que contiene información sobre un
   * artículo de noticias.
   */
  insertaScriptNoticia(noticia: any) {
    if (this.existeScript("NewsArticleStructData")) {
      this.borraScript("NewsArticleStructData");
    }

    const date = this.dateToIso(noticia.fecha);
    const dateEditada = this.dateToIso(noticia.fechaEdicion);

    const socialMedia = this.getRedes();
    if (socialMedia) {
      socialMedia.pipe(first()).subscribe(() => {
        const json = {
          "@context": "http://schema.org",
          "@type": "NewsArticle",
          url: noticia.urlNoticia,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": noticia.urlNoticia,
          },
          headline: noticia.tituloVideo,
          description: noticia.description,
          datePublished: date,
          dateModified: dateEditada,
          image: [this.creaImageSchema(noticia.urlGenerica)],
          author: this.creaAuthorSchema(noticia),
          publisher: this.creaOrganizationSchema(),
        };
        this.insertaScript(json, "NewsArticleStructData");
      });
    }
  }

  insertaScriptVideo(tipo: string, noticia: any, customVideo?: ElementRef<HTMLVideoElement>) {
    type interactionType = {
      "@type": "InteractionCounter";
      interactionType: { "@type": "WatchAction" };
      userInteractionCount: number;
    };
    type dataType = {
      "@context": "https://schema.org";
      "@type": "VideoObject";
      name: string;
      description: string;
      thumbnailUrl: string;
      uploadDate: string;
      duration: string;
      contentUrl?: string;
      embedUrl?: string;
      author: {
        "@type": "Person";
        name: string;
        url?: string;
      };
      interactionStatistic?: interactionType;
    };
    if (this.existeScript("VideoStructData")) {
      this.borraScript("VideoStructData");
    }

    switch (tipo) {
      case "youtube":
        fetch(`https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${this.getVideoId(noticia?.urlVideo, tipo)}&part=statistics%2Csnippet%2CcontentDetails&key=${this.API_KEY}`)
          .then((response) => response.json())
          .then((data) => {
            const datos = {
              "@context": "https://schema.org",
              "@type": "VideoObject",
              name: data.items[0].snippet.title,
              author: {
                "@type": "Person",
                name: data.items[0].snippet.channelTitle,
                url: `https://youtube.com/channel/${data.items[0].snippet.channelId}`,
              },
              uploadDate: data.items[0].snippet.publishedAt,
              thumbnailUrl: data.items[0].snippet.thumbnails["default"].url,
              duration: data.items[0].contentDetails.duration,
              embedUrl: noticia?.urlVideo,
              interactionStatistic: {
                "@type": "InteractionCounter",
                interactionType: { "@type": "WatchAction" },
                userInteractionCount: data.items[0].statistics.viewCount,
              },
            } as dataType;
            if (!this.existeScript("VideoStructData")) {
              this.insertaScript(datos, "VideoStructData");
            }
          })
          .catch(() => {
            //Control de error para cuando la api de YouTube no funcione, los videos estén en privado, etc...
          });

        break;

      case "dailymotion":
        fetch(`https://api.dailymotion.com/video/${this.getVideoId(noticia?.urlVideo, tipo)}?fields=title,owner.screenname,created_time,thumbnail_url,duration,views_total`)
          .then((response) => response.json())
          .then((data) => {
            const datos = {
              "@context": "https://schema.org",
              "@type": "VideoObject",
              name: data.title,
              author: {
                "@type": "Person",
                name: data["owner.screenname"],
                url: `https://www.dailymotion.com/${data["owner.screenname"]}`,
              },
              uploadDate: new Date(data.created_time * 1000).toISOString(),
              thumbnailUrl: data.thumbnail_url,
              duration: `PT${data.duration}S`,
              interactionStatistic: {
                "@type": "InteractionCounter",
                interactionType: { "@type": "WatchAction" },
                userInteractionCount: data.views_total,
              },
            } as dataType;
            this.insertaScript(datos, "VideoStructData");
          })
          .catch(() => {
            //Control de error para cuando la api de YouTube no funcione, los videos estén en privado, etc...
          });

        break;

      case "custom":
        customVideo.nativeElement.addEventListener("loadedmetadata", () => {
          const date = this.dateToIso(noticia.fecha);
          const datos = {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: noticia?.tituloSeo,
            author: this.creaAuthorSchema(noticia),
            uploadDate: date,
            thumbnailUrl: noticia?.urlGenerica,
            duration: `PT${Math.floor(customVideo.nativeElement.duration)}S`,
          };
          this.insertaScript(datos, "VideoStructData");
        });
        break;
    }
  }

  /**
   * La función `insertaScript` crea y agrega un elemento `<script>` en la `<head>` del documento,
   * conteniendo la informacion estructurada para Google del objeto WebSite.
   * @see https://schema.org/WebSite
   * @returns La función no tiene declaración de retorno.
   */
  insertaScriptWebsite(): void {
    if (this.existeScript("WebSiteStructData")) {
      return;
    }
    const rutaLogo = `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.logoMain}`;

    const socialMedia = this.getRedes();
    if (socialMedia) {
      socialMedia.pipe(first()).subscribe(() => {
        const webData: WebSiteStruct = {
          "@type": "WebSite",
          name: this.constantesGlobales.siteName,
          description: this.constantesGlobales.metaTag,
          image: this.creaImageSchema(rutaLogo),
          url: this.constantesGlobales.imagenIconoCuadrado,
          creator: this.creaOrganizationSchema(),
          copyrightHolder: {
            ...this.creaOrganizationSchema(),
            "@id": "",
          },
        };

        this.insertaScript(webData, "WebSiteStructData");
      });
    }
  }

  /**
   * La función "insertaScriptOrganization" crea e inserta un script para NewsMediaOrganization si aún
   * no existe.
   * @returns La función `insertaScriptOrganization()` devuelve `void`, lo que significa que no
   * devuelve ningún valor.
   */
  insertaScriptOrganization(): void {
    if (this.existeScript("NewsMediaStructData")) {
      return;
    }
    let organizationStruct;
    const socialMedia = this.getRedes();
    if (socialMedia) {
      socialMedia.pipe(first()).subscribe(() => {
        organizationStruct = {
          "@type": "NewsMediaOrganization",
          ...this.creaOrganizationSchema(),
        };
        if (organizationStruct) this.insertaScript(organizationStruct, "NewsMediaStructData");
      });
    }
  }

  /**
   * La función `insertaScriptPerson` inserta un script con información sobre una persona, como su
   * imagen, nombre y puesto de trabajo, en el documento HTML si aún no existe.
   * @param idAutor - El parámetro `idAutor` es el ID del autor para quien queremos insertar un script.
   * @returns una Promesa de tipo nula.
   */
  async insertaScriptPerson(idAutor): Promise<void> {
    if (this.existeScript("PersonStructData")) {
      this.borraScript("PersonStructData");
    }
    let personStruct: PersonStruct;
    await this.noticiasService
      .getAutorByUserId(idAutor)
      ?.pipe(first())
      .subscribe((autor) => {
        if (!autor) return;
        personStruct = {
          "@type": "Person",
          image: this.creaImageSchema(autor.url_foto_v2),
          name: autor.nombre,
          jobTitle: "Periodista",
        };
        if (personStruct) this.insertaScript(personStruct, "PersonStructData");
      });
  }

  /**
   * La función 'insertaScriptBreadcrumb' de TypeScript genera una estructura de migas de navegación para un sitio web basada en
   * la ruta URL actual.
   */
  insertaScriptBreadcrumb(): void {
    if (this.existeScript("BreadcrumbStructData")) {
      this.borraScript("BreadcrumbStructData");
    }

    const currentPath = this.platformService.getLocationHostname();
    const allSegments = currentPath.split("/").filter((segment) => segment);

    // Se separan los segmentos que son numéricos (probablemente IDs) y los que no
    const nonIDSegments = allSegments.filter((segment) => !/^\d+$/.test(segment));
    const itemListElement = nonIDSegments.map((segment, index) => {
      const pathSoFar = this.constantesGlobales.siteDomain + "/" + allSegments.slice(0, allSegments.indexOf(segment) + 1).join("/");

      return {
        "@type": "ListItem",
        position: index + 2,
        name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ") || "Inicio",
        item: pathSoFar,
      };
    });

    itemListElement.unshift({
      "@type": "ListItem",
      position: 1,
      name: "Inicio",
      item: this.constantesGlobales.siteDomain,
    });

    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement,
    };

    this.insertaScript(breadcrumbData, "BreadcrumbStructData");
  }

  // FUNCIONES MANIPULACIÓN DOM

  /**
   * La función inserta un elemento de secuencia de comandos con datos JSON en el encabezado del
   * documento.
   * @param data - El parámetro `data` es el objeto JSON que desea insertar como elemento de secuencia
   * de comandos en el documento HTML. Contiene los datos estructurados que desea incluir en la página.
   * @param {string} idScript - El parámetro `idScript` es una cadena que representa el atributo ID del
   * elemento del script que se creará. Este ID se puede usar para hacer referencia al elemento de
   * secuencia de comandos más adelante si es necesario.
   */
  private insertaScript(data, idScript: string) {
    const scriptElement = this.renderer2.createElement("script") as HTMLScriptElement;
    this.renderer2.setAttribute(scriptElement, "type", "application/ld+json");
    this.renderer2.setAttribute(scriptElement, "id", idScript);
    this.renderer2.setProperty(scriptElement, "textContent", JSON.stringify(data));
    this.renderer2.appendChild(this.doc.head, scriptElement);
  }

  /**
   * La función `borraScript` elimina un elemento de script del encabezado del documento.
   * @param {string} idScript - El parámetro `idScript` es una cadena que representa la identificación
   * del elemento del script que desea eliminar del encabezado del documento.
   */
  borraScript(idScript: string) {
    if (this.platformService.isBrowser()) {
      const el = this.doc.getElementById(`${idScript}`);
      if (el) el.remove();
    }
  }

  /**
   * La función "existeScript" comprueba si existe un elemento con una id en el documento.
   * @param {string} id - El parámetro "id" es una cadena que representa el atributo id de un elemento
   * HTML.
   * @returns La función `existeScript` devuelve un valor booleano.
   */
  private existeScript(id: string): boolean {
    return !!this.doc.getElementById(id);
  }

  /**
   * La función convierte una fecha determinada a su representación de cadena ISO.
   * @param {any} fecha - El parámetro "fecha" es de tipo "cualquiera", lo que significa que puede
   * aceptar cualquier tipo de datos.
   * @returns una cadena en formato ISO.
   */
  dateToIso(fecha: any): string {
    // Manejo de valores nulos, undefined, vacíos o no válidos
    if (fecha == null || fecha === undefined || fecha === "" || typeof fecha === "undefined") {
      return null;
    }

    // Manejo de timestamps numéricos
    if (typeof fecha === "number" && !isNaN(fecha)) {
      const date = new Date(fecha);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toISOString();
    }

    // Convertir a string si no es un número ni string
    let fechaStr: string;
    if (typeof fecha !== "string") {
      fechaStr = String(fecha);
    } else {
      fechaStr = fecha;
    }

    // Verificar si fechaStr es inválido después de la conversión
    if (!fechaStr || fechaStr === "undefined" || fechaStr === "null" || typeof fechaStr !== "string") {
      return null;
    }

    // Intentar normalizar la fecha reemplazando espacios por 'T'
    const normalizedDate = fechaStr.replace(/\s/g, "T");

    // Validar si la fecha es válida
    const date = new Date(normalizedDate);
    if (isNaN(date.getTime())) {
      return null;
    }

    // Devolver la fecha en formato ISO
    return date.toISOString();
  }

  getVideoId(url: string, videoType: string): string | null {
    if (videoType === "youtube") {
      const regExp = /^.*(youtu.be\/|v\/|e\/|u\/\w+\/|embed\/|v=)([^#\&\?]*).*/;
      const match = url.match(regExp);

      if (match && match[2].length === 11) {
        return match[2];
      } else {
        return null;
      }
    } else if (videoType === "dailymotion") {
      const pattern = /https:\/\/www\.dailymotion\.com\/embed\/video\/(\w+)/;
      const match = url.match(pattern);
      return match && match[1] ? match[1] : null;
    }
  }
}
